import { createAsyncThunk, createAction } from '@reduxjs/toolkit'
import {
  LedgerEntry,
  InvoiceEntry,
  PaymentEntry,
  PaymentType,
  BillingNote,
  BillingPaymentNotePayload,
  BillingAttachment,
  Patient,
} from '@dataTypes/billing.types'
import type { CreateInvoiceEntryRequest } from '@services/api/billing/invoice.api'
import type { CreateCashPaymentEntryRequest } from '@services/api/billing/payment.api'
import type {
  PatientARAnalytics,
  ARAgingSummaryReport,
  SidePanelAnalytics,
} from '@services/api/billing/analytics.api'
import type {
  TransactionEntry,
  TransactionQueryFilters,
} from '@services/api/billing/transaction.api'
import * as billingApi from '@services/api/billing/billing.api'
import { RootState } from '@store/index'

export type BillingPaginationPayload = Partial<{
  currentPage: number
  pageSize: number
  sort: string
}>

export const setLedgerPagination = createAction<BillingPaginationPayload>(
  'billing/setLedgerPagination',
)
export const setInvoicePagination = createAction<BillingPaginationPayload>(
  'billing/setInvoicePagination',
)
export const setPaymentPagination = createAction<BillingPaginationPayload>(
  'billing/setPaymentPagination',
)
export const setTransactionPagination = createAction<BillingPaginationPayload>(
  'billing/setTransactionPagination',
)

export const fetchLedgerEntries = createAsyncThunk<
  { entries: LedgerEntry[]; count: number },
  billingApi.BillingLedgerFilters | void,
  { rejectValue: string; state: RootState }
>(
  'api/billing/fetch/ledger',
  async (paginationParams, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const patientId = state.patient.selectedPatient?.patient_id
      const clinicId =
        state.clinic.selectedClinic?.clinic_id ||
        localStorage.getItem('clinic_id')
      const { currentPage, pageSize } = state.billing.ledgerPagination
      const filters: billingApi.BillingLedgerFilters = {
        ...(paginationParams ?? {}),
      }
      if (patientId) filters.patient_id = patientId
      if (clinicId) filters.clinic_id = clinicId
      if (filters.limit === undefined) {
        filters.limit = pageSize
      }
      if (filters.offset === undefined) {
        filters.offset = (currentPage - 1) * pageSize
      }
      filters.sort =
        state.billing.ledgerPagination.sort ?? 'appointmentDate:DESC'

      return await billingApi.fetchLedger(
        Object.keys(filters).length > 0 ? filters : undefined,
      )
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to fetch ledger entries'
      return rejectWithValue(message)
    }
  },
)

export const fetchPatientARAnalytics = createAsyncThunk<
  PatientARAnalytics,
  void,
  { rejectValue: string; state: RootState }
>(
  'api/billing/fetch/patientARAnalytics',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const patientId = state.patient.selectedPatient?.patient_id
      const clinicId =
        state.clinic.selectedClinic?.clinic_id ||
        localStorage.getItem('clinic_id')

      return await billingApi.fetchPatientARAnalytics(
        patientId,
        clinicId ?? undefined,
      )
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to fetch patient AR analytics'
      return rejectWithValue(message)
    }
  },
)

export const fetchInsuranceARAnalytics = createAsyncThunk<
  PatientARAnalytics,
  void,
  { rejectValue: string; state: RootState }
>(
  'api/billing/fetch/insuranceARAnalytics',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const patientId = state.patient.selectedPatient?.patient_id
      const clinicId =
        state.clinic.selectedClinic?.clinic_id ||
        localStorage.getItem('clinic_id')

      return await billingApi.fetchInsuranceARAnalytics(
        patientId,
        clinicId ?? undefined,
      )
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to fetch insurance AR analytics'
      return rejectWithValue(message)
    }
  },
)

export const fetchARAging = createAsyncThunk<
  ARAgingSummaryReport,
  void,
  { rejectValue: string; state: RootState }
>('api/billing/fetch/arAging', async (_, { rejectWithValue, getState }) => {
  try {
    const state = getState()
    const patientId = state.patient.selectedPatient?.patient_id
    const clinicId =
      state.clinic.selectedClinic?.clinic_id ||
      localStorage.getItem('clinic_id')

    return await billingApi.fetchARAging(patientId, clinicId ?? undefined)
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : 'Failed to fetch AR aging report'
    return rejectWithValue(message)
  }
})

export const fetchSidePanelAnalytics = createAsyncThunk<
  SidePanelAnalytics,
  void,
  { rejectValue: string; state: RootState }
>(
  'api/billing/fetch/sidePanelAnalytics',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const patientId = state.patient.selectedPatient?.patient_id
      const clinicId =
        state.clinic.selectedClinic?.clinic_id ||
        localStorage.getItem('clinic_id')

      if (!patientId) {
        return rejectWithValue('No patient selected')
      }

      return await billingApi.fetchSidePanelAnalytics(
        patientId,
        clinicId ?? undefined,
      )
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to fetch side panel analytics'
      return rejectWithValue(message)
    }
  },
)

export const fetchPatients = createAsyncThunk<Patient[], void>(
  'api/billing/fetch/patients',
  async (_, { rejectWithValue }) => {
    try {
      return await billingApi.fetchPatientsForBilling()
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch patients'
      return rejectWithValue(message)
    }
  },
)

export const fetchInvoiceEntries = createAsyncThunk<
  { entries: InvoiceEntry[]; count: number },
  billingApi.BillingInvoiceFilters | void,
  { rejectValue: string; state: RootState }
>(
  'api/billing/fetch/invoices',
  async (paginationParams, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const selectedPatientId = state.patient.selectedPatient?.patient_id
      const clinicId =
        state.clinic.selectedClinic?.clinic_id ||
        localStorage.getItem('clinic_id')
      const { currentPage, pageSize } = state.billing.invoicePagination
      const filters: billingApi.BillingInvoiceFilters = {
        ...(paginationParams ?? {}),
      }
      // Explicit `patient_id` (e.g. Add Invoice Modal / family member) must win over chart patient
      if (!filters.patient_id && selectedPatientId) {
        filters.patient_id = selectedPatientId
      }
      if (clinicId) filters.clinic_id = clinicId
      if (filters.limit === undefined) {
        filters.limit = pageSize
      }
      if (filters.offset === undefined) {
        filters.offset = (currentPage - 1) * pageSize
      }
      filters.sort = state.billing.invoicePagination.sort ?? 'invoiceDate:DESC'

      return await billingApi.fetchInvoices(filters)
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to fetch invoice entries'
      return rejectWithValue(message)
    }
  },
)

export const fetchPaymentHistoryEntries = createAsyncThunk<
  { entries: PaymentEntry[]; count: number },
  billingApi.BillingPaymentFilters | void,
  { rejectValue: string; state: RootState }
>(
  'api/billing/fetch/paymentHistory',
  async (paginationParams, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const patientId = state.patient.selectedPatient?.patient_id
      const clinicId =
        state.clinic.selectedClinic?.clinic_id ||
        localStorage.getItem('clinic_id')
      const { currentPage, pageSize } = state.billing.paymentPagination
      const filters: billingApi.BillingPaymentFilters = {
        ...(paginationParams ?? {}),
      }
      if (patientId) filters.patient_id = patientId
      if (clinicId) filters.clinic_id = clinicId
      if (filters.limit === undefined) {
        filters.limit = pageSize
      }
      if (filters.offset === undefined) {
        filters.offset = (currentPage - 1) * pageSize
      }
      filters.sort = state.billing.paymentPagination.sort ?? 'paymentDate:DESC'

      return await billingApi.fetchPaymentHistory(
        Object.keys(filters).length > 0 ? filters : undefined,
      )
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to fetch payment history entries'
      return rejectWithValue(message)
    }
  },
)

export const createInvoice = createAsyncThunk<
  void,
  CreateInvoiceEntryRequest,
  { rejectValue: string }
>('api/billing/create/invoice', async (payload, { rejectWithValue }) => {
  try {
    await billingApi.createInvoice(payload)
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : 'Failed to create invoice entry'
    return rejectWithValue(message)
  }
})

export const deleteInvoiceEntry = createAsyncThunk<string, string>(
  'api/billing/delete/invoiceEntry',
  async (invoiceId, { rejectWithValue }) => {
    try {
      await billingApi.deleteInvoice(invoiceId)
      return invoiceId
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to delete invoice'
      return rejectWithValue(message)
    }
  },
)

export const addPaymentEntry = createAsyncThunk<
  InvoiceEntry,
  InvoiceEntry,
  { rejectValue: string; state: RootState }
>(
  'api/billing/add/payment',
  async (invoice, { fulfillWithValue, rejectWithValue, getState }) => {
    // Check if invoice is already in selectedInvoices
    const state = getState()
    const isDuplicate = state.billing.selectedInvoices.some(
      (inv) => inv.invoiceNo === invoice.invoiceNo,
    )

    if (isDuplicate) {
      return rejectWithValue('Invoice is already selected for payment')
    }

    // Local-only operation - selectedInvoices are manually selected invoices, not fetched from backend
    // When a payment is actually recorded, use recordPayment action which calls createCashPaymentEntryApi
    return fulfillWithValue(invoice)
  },
)

export const clearSelectedInvoices = createAction(
  'api/billing/clear/selectedInvoices',
)

export const removeInvoiceFromSelection = createAsyncThunk<string, string>(
  'api/billing/remove/invoiceFromSelection',
  async (invoiceNo, { fulfillWithValue }) => {
    return fulfillWithValue(invoiceNo)
  },
)

export const recordPayment = createAsyncThunk<
  void,
  {
    invoiceIds: string[]
    amountPaid: number
    paidBy: string
    createdBy: string
    method: PaymentType
    clinicId: string
    referenceNumber?: string
    notes?: BillingPaymentNotePayload[]
  },
  { rejectValue: string }
>('api/billing/record/payment', async (payload, { rejectWithValue }) => {
  try {
    const paymentMethod = payload.method || PaymentType.CASH
    if (paymentMethod !== PaymentType.CASH) {
      return rejectWithValue(
        'Only cash payments are supported through this endpoint',
      )
    }

    const requestPayload: CreateCashPaymentEntryRequest = {
      amount: payload.amountPaid,
      invoice_ids: payload.invoiceIds,
      paid_by: payload.paidBy,
      created_by: payload.createdBy,
      clinic_id: payload.clinicId,
      ...(payload.referenceNumber && {
        reference_number: payload.referenceNumber,
      }),
      ...(payload.notes &&
        payload.notes.length > 0 && { notes: payload.notes }),
    }

    await billingApi.createCashPayment(requestPayload)
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : 'Failed to record payment'
    return rejectWithValue(message)
  }
})

export const updateLedgerNotes = createAsyncThunk<
  LedgerEntry,
  { appointmentId: string; notes: BillingNote[] },
  { rejectValue: string; state: RootState }
>(
  'api/billing/ledger/updateNotes',
  async ({ appointmentId, notes }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const ledgerEntry = state.billing.ledger.find(
        (entry: LedgerEntry) => entry.appointmentId === appointmentId,
      )
      if (!ledgerEntry) {
        return rejectWithValue('Ledger entry not found')
      }
      const updated: LedgerEntry = {
        ...ledgerEntry,
        notes: [...notes],
      }
      return updated
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to update notes'
      return rejectWithValue(message)
    }
  },
)

/** Updates ledger attachments in Redux only. Client-only until a backend endpoint exists. */
export const updateLedgerAttachments = createAsyncThunk<
  LedgerEntry,
  { appointmentId: string; attachments: BillingAttachment[] },
  { rejectValue: string; state: RootState }
>(
  'api/billing/ledger/updateAttachments',
  async ({ appointmentId, attachments }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const ledgerEntry = state.billing.ledger.find(
        (entry: LedgerEntry) => entry.appointmentId === appointmentId,
      )
      if (!ledgerEntry) {
        return rejectWithValue('Ledger entry not found')
      }
      const updated: LedgerEntry = {
        ...ledgerEntry,
        attachments: [...attachments],
      }
      return updated
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to update attachments'
      return rejectWithValue(message)
    }
  },
)

export const fetchTransactionEntries = createAsyncThunk<
  { entries: TransactionEntry[]; count: number },
  TransactionQueryFilters | void,
  { rejectValue: string; state: RootState }
>(
  'api/billing/fetch/transactions',
  async (filters, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const patientId = state.patient.selectedPatient?.patient_id
      const clinicId =
        state.clinic.selectedClinic?.clinic_id ||
        localStorage.getItem('clinic_id')
      const { currentPage, pageSize } = state.billing.transactionPagination
      const queryFilters: TransactionQueryFilters = {
        ...(filters || {}),
      }
      if (!queryFilters.patient_id && patientId) {
        queryFilters.patient_id = patientId
      }
      if (!queryFilters.clinic_id && clinicId) {
        queryFilters.clinic_id = clinicId
      }
      if (queryFilters.limit === undefined) {
        queryFilters.limit = pageSize
      }
      if (queryFilters.offset === undefined) {
        queryFilters.offset = (currentPage - 1) * pageSize
      }
      if (!queryFilters.sort) {
        queryFilters.sort = 'date:DESC'
      }

      return await billingApi.fetchTransactions(queryFilters)
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to fetch transaction entries'
      return rejectWithValue(message)
    }
  },
)
